import { Button, Dropdown, Input, Modal } from "@amzn/alchemy-components-react";
import { useTranslation } from "react-i18next";
import { AlertType, ModalCloseSource, TenantID, UserType } from "src/common/enums";
import { AlertBar } from "src/components/alert-bar";
import React, { useCallback, useEffect, useState } from "react";
import { logger } from "src/logger";
import { isEmptyString } from "src/common/functions";
import { useLazyQuery, useMutation } from "@apollo/client";
import { GET_USER_INFO_BY_ALIAS_QUERY, ADD_PRINCIPAL_SCOPE_MUTATION } from "src/common/gql-operations";
import { NETWORK_ONLY } from "src/common/constants";

import { FallbackSpinner } from "src/components/fallback-spinner";
import initialMetricsPublisher from "src/metrics";
import * as KatalMetrics from "@amzn/katal-metrics";

const cloudWatchDimensions = [
    new KatalMetrics.Metric.String('modal', 'add-user'),
];

const additionalMetricsContext = new KatalMetrics.Context({ cloudWatchDimensions });
const graphqlClientMetricsPublisher =
    initialMetricsPublisher.newChildActionPublisherForMethod('graphql-client', additionalMetricsContext);

interface AddUserModalProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    scope: string;
}

interface FormState {
    systemAccessCredential: string;
    selectedUserType: string;
    email: string;
}

interface AlertState {
    type?: AlertType;
    header?: string;
    message?: string;
}

interface ErrorState {
    message: string | null;
}

interface UserInfo {
    alias: string;
    principalArn: string;
    firstName: string;
    lastName: string;
    jobType: string;
    isActive: boolean;
}

import { InlineError } from "src/components/inline-error";

type ModalStep = 'form' | 'confirmation';

export const AddUserModal = (props: AddUserModalProps) => {
    const { t } = useTranslation();

    const [formState, setFormState] = useState<FormState>({
        systemAccessCredential: '',
        selectedUserType: '',
        email: ''
    });

    const requiresEmail = (userType: string): boolean => {
        return userType === UserType.ASSOCIATE ||
            userType === UserType.USER_ADMIN_PRIMARY ||
            userType === UserType.USER_ADMIN_SECONDARY;
    };

    const isValid = !isEmptyString(formState.systemAccessCredential)
        && !isEmptyString(formState.selectedUserType)
        && (!requiresEmail(formState.selectedUserType) || !isEmptyString(formState.email));

    const [alertState, setAlertState] = useState<AlertState>({
        type: undefined,
        header: undefined,
        message: undefined
    });

    const [errorState, setErrorState] = useState<ErrorState>({ message: null });
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [modalStep, setModalStep] = useState<ModalStep>('form');

    const [getUserInfoByAlias, { loading: getUserInfoLoading }] = useLazyQuery(GET_USER_INFO_BY_ALIAS_QUERY, {
        fetchPolicy: NETWORK_ONLY,
        onCompleted: (data) => {
            logger.info("getUserInfoByAlias succeeded", data);
            graphqlClientMetricsPublisher.publishCounterMonitor('get-user-info-by-alias.SUCCESS', 1);

            const persons = data?.getUserInfoByAlias?.persons;
            if (persons && persons.length > 0) {
                const person = persons[0];
                if (!person.isActive) {
                    setError(t('user-is-not-active', { alias: formState.systemAccessCredential }));
                    return;
                }
                setUserInfo(person);
                setModalStep('confirmation');
                clearError();
            } else {
                setError(t('no-user-found-with-alias', { alias: formState.systemAccessCredential }));
            }
        },
        onError: error => {
            logger.error("Call to getUserInfoByAlias failed!", error);
            graphqlClientMetricsPublisher.publishCounterMonitor('get-user-info-by-alias.ERROR', 1);
            setError(error.message);
        }
    });

    const [addPrincipalScope, { loading: addPrincipalScopeLoading }] = useMutation(ADD_PRINCIPAL_SCOPE_MUTATION, {
        fetchPolicy: NETWORK_ONLY,
        onCompleted: () => {
            logger.info("addPrincipalScope succeeded");
            graphqlClientMetricsPublisher.publishCounterMonitor('add-principal-scope.SUCCESS', 1);
            showSuccessAlert(t('user-added-successfully'), t('user-has-been-added-to-scope', { scope: props.scope, role: formState.selectedUserType }));
            setTimeout(() => {
                handleModalClose(ModalCloseSource.SUCCESS);
                window.location.reload();
            }, 3000);
        },
        onError: error => {
            logger.error("Call to addPrincipalScope failed!", error);
            graphqlClientMetricsPublisher.publishCounterMonitor('add-principal-scope.ERROR', 1);
            setError(error.message);
        }
    });

    const resetAlertBar = () => {
        setAlertState({
            type: undefined,
            header: undefined,
            message: undefined
        });
    };

    const setError = (message: string) => {
        setErrorState({ message });
    };

    const clearError = () => {
        setErrorState({ message: null });
    };

    const resetForm = useCallback(() => {
        setFormState({
            systemAccessCredential: '',
            selectedUserType: '',
            email: ''
        });
        setUserInfo(null);
        setModalStep('form');
        clearError();
        resetAlertBar();
    }, []);

    useEffect(() => {
        if (!props.open) {
            resetForm();
        }
    }, [props.open, resetForm]);

    const handleModalClose = (source: ModalCloseSource) => {
        logger.info(`Add User Modal closing from: ${source}`);
        resetForm();
        props.setOpen(false);
    };

    const showSuccessAlert = (header: string, message: string) => {
        setAlertState({
            type: AlertType.success,
            header,
            message
        });
    };

    const handleSystemAccessCredentialInput = (value: string) => {
        setFormState({
            ...formState,
            systemAccessCredential: value
        });
    };

    const handleUserTypeSelection = (userType: string) => {
        logger.info(`Selected User Type: ${userType}`);
        setFormState({
            ...formState,
            selectedUserType: userType,
            email: requiresEmail(userType) ? formState.email : ''
        });
    };

    const handleEmailInput = (value: string) => {
        setFormState({
            ...formState,
            email: value
        });
    };

    const handleSubmit = () => {
        clearError();

        if (!isValid) {
            setError(t('please-fill-in-all-fields'));
            return;
        }

        logger.info(`Submitting with system access credential: ${formState.systemAccessCredential}, UserType: ${formState.selectedUserType}, Scope: ${props.scope}`);

        graphqlClientMetricsPublisher.publishCounterMonitor('get-user-info-by-alias.INVOCATION', 1);
        getUserInfoByAlias({
            variables: {
                getUserInfoByAliasInput: {
                    tenantId: TenantID.AFTX,
                    aliases: [formState.systemAccessCredential]
                }
            }
        });
    };

    const handleConfirm = () => {
        clearError();

        if (!userInfo) {
            setError(t('user-info-not-available'));
            return;
        }

        logger.info(`Confirming add user: ${userInfo.alias} to scope ${props.scope}`);

        graphqlClientMetricsPublisher.publishCounterMonitor('add-principal-scope.INVOCATION', 1);
        addPrincipalScope({
            variables: {
                addPrincipalScopeInput: {
                    tenantId: TenantID.AFTX,
                    principalArn: userInfo.principalArn,
                    scope: props.scope,
                    userType: formState.selectedUserType,
                    ...(requiresEmail(formState.selectedUserType) && { email: formState.email })
                }
            }
        });
    };

    const handleBack = () => {
        setModalStep('form');
        setUserInfo(null);
        clearError();
        resetAlertBar();
    };

    const userTypeOptions = [
        { label: t('select-user-type'), value: '' },
        { label: UserType.ASSOCIATE, value: UserType.ASSOCIATE },
        { label: UserType.USER_ADMIN_PRIMARY, value: UserType.USER_ADMIN_PRIMARY },
        { label: UserType.USER_ADMIN_SECONDARY, value: UserType.USER_ADMIN_SECONDARY },
        { label: UserType.ABBM_SECONDARY, value: UserType.ABBM_SECONDARY }
    ];

    const renderFormStep = () => (
        <>
            {errorState.message && <InlineError message={errorState.message} />}

            <div className="row mb-3">
                <div className="col">
                    <Input
                        id="system-access-credential-input"
                        label={t('system-access-credentials')}
                        type="text"
                        value={formState.systemAccessCredential}
                        onInput={e => handleSystemAccessCredentialInput(e.target.value)}
                    />
                </div>
            </div>

            <div className="row mb-3">
                <div className="col">
                    <Dropdown
                        id="user-type-select"
                        label={t('select-user-type')}
                        options={userTypeOptions}
                        onChange={e => handleUserTypeSelection(e.target.value)}
                        value={formState.selectedUserType}
                    />
                </div>
            </div>

            {requiresEmail(formState.selectedUserType) && (
                <div className="row mb-3">
                    <div className="col">
                        <Input
                            id="email-input"
                            label={t('email')}
                            type="email"
                            value={formState.email}
                            onInput={e => handleEmailInput(e.target.value)}
                        />
                    </div>
                </div>
            )}

            <div className="row d-flex justify-content-center" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
                <Button
                    id="add-user-modal-cancel-button"
                    size="lg"
                    label={t('cancel')}
                    onClick={() => handleModalClose(ModalCloseSource.CANCEL)}
                    className="mx-1"
                />
                <Button
                    id="add-user-modal-submit-button"
                    size="lg"
                    label={t('submit')}
                    variant="action"
                    disabled={!isValid || getUserInfoLoading}
                    onClick={handleSubmit}
                    className="mx-1"
                />
            </div>
        </>
    );

    const renderConfirmationStep = () => (
        <>
            {errorState.message && <InlineError message={errorState.message} />}

            <div className="row mb-3">
                <div className="col">
                    <p>{t('please-confirm-add-user-to-scope', { scope: props.scope })}</p>
                </div>
            </div>

            <div className="row mb-3">
                <div className="col">
                    <table className="alchemy-table bordered" style={{ width: '100%', tableLayout: 'auto' }}>
                        <thead>
                        <tr>
                            <th>{t('system-access-credentials')}</th>
                            <th>{t('name')}</th>
                            <th>{t('job-type')}</th>
                            <th>{t('user-type')}</th>
                            {requiresEmail(formState.selectedUserType) && <th>{t('email')}</th>}
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                            <td>{userInfo?.alias}</td>
                            <td>{userInfo?.firstName} {userInfo?.lastName}</td>
                            <td>{userInfo?.jobType}</td>
                            <td>{formState.selectedUserType}</td>
                            {requiresEmail(formState.selectedUserType) && <td>{formState.email}</td>}
                        </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="row d-flex justify-content-center" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
                <Button
                    id="add-user-modal-back-button"
                    size="lg"
                    label={t('back')}
                    onClick={handleBack}
                    className="mx-1"
                />
                <Button
                    id="add-user-modal-confirm-button"
                    size="lg"
                    label={t('confirm')}
                    variant="action"
                    disabled={addPrincipalScopeLoading}
                    onClick={handleConfirm}
                    className="mx-1"
                />
            </div>
        </>
    );

    const isLoading = getUserInfoLoading || addPrincipalScopeLoading;

    return (
        <Modal
            id="add-user-modal"
            className="mb-1 add-user-modal"
            header={t('add-user-to-scope', { scope: props.scope })}
            open={props.open}
            size="x-large"
            onClose={(e: React.MouseEvent) => {
                if (e.target === e.currentTarget) {
                    handleModalClose(ModalCloseSource.OVERLAY);
                } else if ((e.target as HTMLElement).closest('.alchemy-modal-close-button')) {
                    handleModalClose(ModalCloseSource.CROSS);
                }
            }}
        >
            {isLoading && <FallbackSpinner />}
            <div className="container-fluid" onClick={(e) => e.stopPropagation()} style={{ paddingTop: '2rem', paddingLeft: '4rem', paddingRight: '4rem' }}>
                {modalStep === 'form' && renderFormStep()}
                {modalStep === 'confirmation' && renderConfirmationStep()}
            </div>
            {alertState.message && alertState.type === AlertType.success && (
                <AlertBar
                    id="add-user-modal-alert-bar"
                    result={alertState.type}
                    dismissible={true}
                    header={alertState.header!}
                    message={alertState.message}
                    reset={resetAlertBar}
                />
            )}
        </Modal>
    );
};
